import React, { useEffect } from 'react';
import './Resume.css';

const Resume: React.FC = () => {
  useEffect(() => {
    document.title = 'Resume | Sourov Roy';
  }, []);
  const education = [
    {
      institution: "Jadavpur University",
      degree: "Bachelor of Computer Science & Engineering",
      year: "2016 - 2020",
      description: "Kolkata, India"
    },
    {
      institution: "Nebadhai High School",
      degree: "Higher Secondary Examination",
      year: "2014 - 2016",
      description: "Kolkata, India"
    }
  ];

  const experience = [
    {
      company: "Deliveroo",
      position: "Software Engineer II",
      duration: "Sep 2024 – Present",
      description: "Hyderabad, India",
      responsibilities: [
        "Onboarded 150K+ restaurants into Google Maps Ads by enriching listings with menu items, delivery time estimates, and service and delivery charges, which improved customer acquisition and resulted in a 1.5% increase in Deliveroo order volume",
        "Enabled 300K+ retail and grocery menu items to be discoverable via Google Shopping Ads across 2M+ postcodes and 3K+ zones with regional availability support in a partner-agnostic manner, driving significant incremental sales",
        "Improved marketing campaign ROI by building a microservice that aggregates key marketing events from multiple internal sources and delivers real-time conversion signals to third-party platforms such as Meta, TikTok, Google, AppsFlyer, and Firebase, helping optimize campaign performance",
        "Conducted 50+ coding and system design interviews for intern, L3, and L4 roles to ensure a high hiring bar, and mentored 3 new hires during onboarding to accelerate ramp-up and integration"
      ]
    },
    {
      company: "Amazon.com",
      position: "Software Engineer",
      duration: "Jul 2020 – Sep 2024",
      description: "Hyderabad, India",
      responsibilities: [
        "Designed and implemented a Return-to-Seller workflow handling 50M packages annually across the EU and post-Brexit UK, automating warehouse interventions and dynamically selecting optimal cross-border return routes, streamlining logistics and reducing operational costs",
        "Redesigned Amazon's transportation invoice auditing system (auditing $16B annually) to be domain-agnostic, enabling use beyond transportation (e.g., real estate) through a complete overhaul of models and database architecture",
        "Built scalable frontend and backend microservices in AWS for data ingestion, invoice auditing, and payment processing",
        "Redesigned Amazon's transportation financial system for government contractors such as Royal Mail UK and USPS, identifying failure points in large invoices and building a new platform capable of systematically processing $200M+ annually, improving reliability and operational efficiency",
        "Developed scalable AWS frontend and backend microservices for a global transportation configuration and credit note management system, accelerating transportation vendor onboarding",
        "Mentored 5 interns and 2 new hires, guiding them to lead pipeline automation initiatives, execute database migrations, resolve long-standing technical debt, and contribute to other key projects"
      ]
    },
    {
      company: "HackerRank.com",
      position: "Problem Setter",
      duration: "Jan 2018 – Jul 2020",
      description: "Remote",
      responsibilities: [
        "Authored 50+ enterprise-grade technical interview problems with comprehensive solutions and robust test cases"
      ]
    },
    {
      company: "Independent",
      position: "Mentor",
      duration: "May 2019 – Present",
      description: "Global",
      responsibilities: [
        "Mentored 500+ students globally (USA, UAE, Germany, Norway, Australia, India), guiding them to achieve academic and professional objectives"
      ]
    }
  ];

  const skills = {
    "Programming Languages": [
      "Java", "Python", "C/C++", "Ruby", "Golang", "JavaScript", "TypeScript", "Rust", "Scala", "Bash", "Shell", "R", "CSS", "HTML"
    ],
    "Web Frameworks": [
      "React", "Angular", "Spring Boot", "Node.js", "Spring", "Django", "Flask", "NestJS"
    ],
    "API & Specifications": [
      "Mulesoft", "Postman", "Swagger", "OpenAPI", "REST", "GraphQL"
    ],
    "Cloud & DevOps": [
      "AWS", "Kubernetes", "Temporal.io", "Docker", "Git/GitHub", "Metaflow", "Argo", "CI/CD", "EKS", "Fargate", "ECS" 
    ],
    "Databases": [
      "ScyllaDB", "MySQL", "PostgreSQL", "DynamoDB", "MongoDB", "Redis", "Elasticsearch", "Aurora", "SQLite"
    ],
    "Machine Learning / AI": [
      "NumPy", "Pandas", "Matplotlib", "Sklearn", "TensorFlow", "Keras", "PyTorch", "Seaborn", "Agentic AI"
    ],
    "Data Engineering": [
      "Apache Spark", "PySpark", "n8n", "Apache Kafka", "Airflow", "Glue", "Athena", "Redshift", "Snowflake"
    ],
    "Core Competencies": [
      "Distributed Systems Design", "DSA", "OOP", "Software Architecture", "Operating Systems", "Design Patterns"
    ],
    "Domain Expertise": [
      "FinTech", "MarkTech", "AdTech", "Logistics", "E-commerce", "IAM", "QuickCommerce", "Algo Trading"
    ]
  };

  return (
    <div className="resume">
      <div className="resume-container">
        <header className="resume-header">
          <div className="resume-header-content">
            <div className="resume-info">
              <h1 className="resume-name">Sourov Roy</h1>
              <p className="resume-title">Software Engineer</p>
              <div className="resume-contact">
                <a href="mailto:info.sourovroy1998@gmail.com" className="contact-item">
                  <span className="contact-icon">✉</span>
                  <span>info.sourovroy1998@gmail.com</span>
                </a>
                <a href="tel:+918981033287" className="contact-item">
                  <span className="contact-icon">📞</span>
                  <span>+91-8981033287</span>
                </a>
                <span className="contact-item">
                  <span className="contact-icon">📍</span>
                  <span>Bangalore, India</span>
                </span>
              </div>
            </div>
            <div className="resume-actions">
              <a href="/static/resume.pdf" download="Sourov_Roy_CV.pdf" className="download-btn">
                Download Resume
              </a>
            </div>
          </div>
        </header>

        <section className="resume-section">
          <h2 className="section-title">Education</h2>
          {education.map((edu, index) => (
            <div key={index} className="education-item">
              <div className="education-header">
                <h3 className="institution">{edu.institution}</h3>
                <span className="year">{edu.year}</span>
              </div>
              <p className="degree">{edu.degree}</p>
              <p className="description">{edu.description}</p>
            </div>
          ))}
        </section>

        <section className="resume-section">
          <h2 className="section-title">Experience</h2>
          {experience.map((exp, index) => (
            <div key={index} className="experience-item">
              <div className="experience-header">
                <h3 className="company">{exp.company}</h3>
                <span className="duration">{exp.duration}</span>
              </div>
              <p className="position">{exp.position}</p>
              <p className="description">{exp.description}</p>
              <ul className="responsibilities">
                {exp.responsibilities.map((resp, respIndex) => (
                  <li key={respIndex}>{resp}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <section className="resume-section">
          <h2 className="section-title">Skills</h2>
          <div className="skills-grid">
            {Object.entries(skills).map(([category, skillList]) => (
              <div key={category} className="skill-category">
                <h3 className="skill-category-title">{category}</h3>
                <div className="skill-tags">
                  {skillList.map((skill, index) => (
                    <span key={index} className="skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Resume;
